import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';
import axios from 'axios';
import apiClient from '../../../pages/keycloak/axiosInstance';

const Form_4 = () => {
    const navigate = useNavigate();

    const baseFields = [
        { label: 'Layup Start Serial No', name: 'layupStartSerialNo' },
        { label: 'Layup End Serial No', name: 'layupEndSerialNo' },
        { label: 'Total Layup Quantity', name: 'totalLayupQty' },
    ];

    const [lines, setLines] = useState([]);
    const [wattages, setWattages] = useState([]);
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('DailyVolumeF4formData');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        
        const parsedData = savedData ? JSON.parse(savedData) : {};
        return {
            date: inspectionDate || parsedData.date || '',
            shift: inspectionShift || parsedData.shift || '',
            lines: parsedData.lines || {},
            submittedLines: parsedData.submittedLines || {},
            modifiedQuantities: parsedData.modifiedQuantities || {}
        };
    });

    const [selectedLine, setSelectedLine] = useState('');
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    const fetchCumulativeData = async (wattage) => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionDate = sessionStorage.getItem('inspectionDate');

        try {
            const response = await apiClient.get(`get_cumulative`, {
                params: {
                    projectId,
                    inspectionSupplier,
                    inspectorName,
                    inspectionDate,
                    wattage
                }
            });
            return response.data.cumulativeMWp || '0';
        } catch (error) {
            console.error(`Error fetching cumulative data for ${wattage}:`, error);
            return '0';
        }
    };

    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const lineList = parsedMetadata.lines?.map(item => item.Lines) || [];
                setLines(lineList);
                if (lineList.length > 0 && !selectedLine) {
                    setSelectedLine(lineList[0]);
                }

                const wattageList = parsedMetadata.dailyVolumeOfQualifiedModules?.map(item => item.Wattage) || [];
                setWattages(wattageList);
            } catch (error) {
                console.error('Error parsing metadata:', error);
            }
        }
    }, []);

    useEffect(() => {
        if (lines.length > 0 && wattages.length > 0) {
            const initializeFormData = async () => {
                const defaultLineData = {
                    ...baseFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
                    packedQuantities: wattages.reduce((acc, wattage) => ({
                        ...acc,
                        [wattage]: {
                            quantity: '',
                            MWp: '',
                            cumulativeMWp: '',
                            apiCumulativeMWp: ''
                        }
                    }), {})
                };

                const inspectionData = sessionStorage.getItem('inspectionData');
                let prefilledLines = {};
                let dailyVolume = null;

                if (inspectionData) {
                    try {
                        const parsedInspectionData = JSON.parse(inspectionData);
                        const inspectionObject = Array.isArray(parsedInspectionData) ? parsedInspectionData[0] : parsedInspectionData;
                        dailyVolume = inspectionObject.dailyVolumeQualifiedModules;

                        if (dailyVolume && dailyVolume.lines && Array.isArray(dailyVolume.lines)) {
                            dailyVolume.lines.forEach((lineData) => {
                                const lineKey = lineData.line;
                                if (lines.includes(lineKey)) {
                                    const packedQuantities = wattages.reduce((acc, wattage) => ({
                                        ...acc,
                                        [wattage]: {
                                            quantity: lineData.packedQuantity?.[wattage]?.quantity || '',
                                            MWp: lineData.packedQuantity?.[wattage]?.MWp || '',
                                            cumulativeMWp: '',
                                            apiCumulativeMWp: ''
                                        }
                                    }), {});

                                    prefilledLines[lineKey] = {
                                        layupStartSerialNo: lineData.layup?.startSerialNo || '',
                                        layupEndSerialNo: lineData.layup?.endSerialNo || '',
                                        totalLayupQty: lineData.layup?.totalQty || '',
                                        packedQuantities
                                    };
                                }
                            });
                        }
                    } catch (error) {
                        console.error('Error parsing inspectionData:', error);
                    }
                }

                const cumulativePromises = wattages.map(wattage => 
                    fetchCumulativeData(wattage).then(cumulativeMWp => ({ wattage, cumulativeMWp }))
                );

                const cumulativeResults = await Promise.all(cumulativePromises);

                setFormData((prevData) => {
                    const newLines = lines.reduce((acc, line) => {
                        const lineData = prefilledLines[line] || prevData.lines[line] || defaultLineData;
                        const updatedPackedQuantities = { ...lineData.packedQuantities };

                        cumulativeResults.forEach(({ wattage, cumulativeMWp }) => {
                            updatedPackedQuantities[wattage] = {
                                ...updatedPackedQuantities[wattage],
                                cumulativeMWp: cumulativeMWp,
                                apiCumulativeMWp: cumulativeMWp
                            };
                        });

                        return {
                            ...acc,
                            [line]: {
                                ...lineData,
                                packedQuantities: updatedPackedQuantities
                            }
                        };
                    }, {});

                    return {
                        ...prevData,
                        lines: newLines,
                        date: dailyVolume?.date || prevData.date,
                        shift: dailyVolume?.shift || prevData.shift
                    };
                });
            };

            initializeFormData();
        }
    }, [lines, wattages]);

    useEffect(() => {
        sessionStorage.setItem('DailyVolumeF4formData', JSON.stringify(formData));
    }, [formData]);

    const handleChange = (e, wattage = null) => {
        const { name, value } = e.target;
        setFormData((prevData) => {
            if (wattage) {
                const isQuantityChange = name === 'quantity';
                const quantity = isQuantityChange ? value : prevData.lines[selectedLine].packedQuantities[wattage].quantity;
                const MWp = isQuantityChange && quantity 
                    ? ((parseFloat(quantity) * parseFloat(wattage)) / 1000000).toFixed(6)
                    : prevData.lines[selectedLine].packedQuantities[wattage].MWp;
                
                const apiCumulativeMWp = prevData.lines[selectedLine].packedQuantities[wattage].apiCumulativeMWp || '0';
                
                let newCumulativeMWp = prevData.lines[selectedLine].packedQuantities[wattage].cumulativeMWp;
                if (isQuantityChange && quantity) {
                    newCumulativeMWp = ((parseFloat(MWp) || 0) + parseFloat(apiCumulativeMWp)).toFixed(6);
                } else if (!quantity) {
                    newCumulativeMWp = apiCumulativeMWp;
                }

                return {
                    ...prevData,
                    lines: {
                        ...prevData.lines,
                        [selectedLine]: {
                            ...prevData.lines[selectedLine],
                            packedQuantities: {
                                ...prevData.lines[selectedLine].packedQuantities,
                                [wattage]: {
                                    ...prevData.lines[selectedLine].packedQuantities[wattage],
                                    [name]: value,
                                    ...(isQuantityChange ? { 
                                        MWp, 
                                        cumulativeMWp: newCumulativeMWp 
                                    } : {})
                                }
                            }
                        }
                    },
                    ...(isQuantityChange ? {
                        modifiedQuantities: {
                            ...prevData.modifiedQuantities,
                            [selectedLine]: {
                                ...prevData.modifiedQuantities[selectedLine],
                                [wattage]: !!quantity
                            }
                        }
                    } : {})
                };
            }
            return {
                ...prevData,
                lines: {
                    ...prevData.lines,
                    [selectedLine]: {
                        ...prevData.lines[selectedLine],
                        [name]: value
                    }
                }
            };
        });
    };

    const handleLineChange = (e) => {
        setSelectedLine(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const hasValidQuantity = Object.values(formData.lines[selectedLine].packedQuantities).some(
            pq => pq.quantity && pq.quantity !== ''
        );

        if (!hasValidQuantity) {
            alert('Please enter at least one Packed Quantity before submitting.');
            return;
        }

        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            dailyVolumeQualifiedModules: {
                date: formData.date.split('-').reverse().join('-'),
                shift: formData.shift,
                lines: lines.map(line => ({
                    line,
                    layup: {
                        startSerialNo: formData.lines[line].layupStartSerialNo,
                        endSerialNo: formData.lines[line].layupEndSerialNo,
                        totalQty: formData.lines[line].totalLayupQty
                    },
                    packedQuantity: Object.keys(formData.lines[line].packedQuantities).reduce((acc, wattage) => ({
                        ...acc,
                        [wattage]: {
                            quantity: formData.lines[line].packedQuantities[wattage].quantity,
                            MWp: formData.lines[line].packedQuantities[wattage].MWp,
                            cumulativeMWp: formData.lines[line].packedQuantities[wattage].cumulativeMWp
                        }
                    }), {})
                }))
            }
        };

        try {
            await submitFormData(payload);
            alert('Form submitted successfully!');
            setFormData((prevData) => ({
                ...prevData,
                submittedLines: {
                    ...prevData.submittedLines,
                    [selectedLine]: true
                }
            }));
        } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit form!');
        }
    };

    if (!formData.lines || !formData.lines[selectedLine] || wattages.length === 0 || lines.length === 0) {
        return <div>Please Check the metadata for Lines...</div>;
    }

    return (
        <div className="p-6 max-w mx-auto bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-bold mb-6 text-center">Daily Volume of Qualified Modules</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-3 md:grid-cols-3 gap-6">
                <div>
                    <label className="block text-sm font-semibold text-gray-700">Date:</label>
                    <input
                        type="text"
                        name="date"
                        value={formData.date}
                        readOnly
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700">Shift:</label>
                    <input
                        type="text"
                        name="shift"
                        value={formData.shift}
                        readOnly
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700">Line:</label>
                    <select
                        value={selectedLine}
                        onChange={handleLineChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                        {lines.map((line) => (
                            <option key={line} value={line}>{line}</option>
                        ))}
                    </select>
                </div>
                {baseFields.map(({ label, name }) => (
                    <div key={name}>
                        <label className="block text-sm font-semibold text-gray-700">{label}:</label>
                        <input
                            type="text"
                            name={name}
                            value={formData.lines[selectedLine][name] || ''}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                ))}
                {wattages.map(wattage => (
                    <React.Fragment key={wattage}>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700">Packed {wattage}Wp Quantity:</label>
                            <input
                                type="number"
                                name="quantity"
                                value={formData.lines[selectedLine].packedQuantities[wattage].quantity || ''}
                                onChange={(e) => handleChange(e, wattage)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700">Packed {wattage}Wp MWp:</label>
                            <input
                                type="number"
                                name="MWp"
                                readOnly
                                value={formData.lines[selectedLine].packedQuantities[wattage].MWp || ''}
                                className="mt-1 block w-full px-3 py-2 border bg-gray-100 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700">Cumulative {wattage}Wp MWp:</label>
                            <input
                                type="text"
                                name="cumulativeMWp"
                                readOnly
                                value={formData.lines[selectedLine].packedQuantities[wattage].cumulativeMWp || ''}
                                className="mt-1 block w-full px-3 py-2 border bg-gray-100 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                    </React.Fragment>
                ))}
                <div className="col-span-3 flex justify-between mt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/inspections/section2/form3')}
                        className="bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded hover:bg-gray-400 focus:outline-none"
                    >
                        Previous
                    </button>
                    <button
                        type="submit"
                        className="bg-[#424752] text-white font-semibold py-2 px-4 rounded hover:bg-gray-600 focus:outline-none"
                    >
                        Submit
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/inspections/section2/form5')}
                        className="bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded hover:bg-gray-400 focus:outline-none"
                    >
                        Next
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Form_4;